import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Calendar, CheckCircle2, X, Clock, MapPin, Loader2, ChevronLeft } from "lucide-react";

export default function BookVisit({ propertyId, propertyTitle, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [note, setNote] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/calendar/${propertyId}/slots?days=14`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Errore caricamento slot"))
            .finally(() => setLoading(false));
    }, [propertyId]);

    const book = async () => {
        if (!selectedSlot) return;
        setSubmitting(true); setError(null);
        try {
            const r = await api.post("/appointments", { property_id: propertyId, start_at: selectedSlot.start_at, note, client_phone: phone });
            setConfirmation(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore prenotazione");
        } finally { setSubmitting(false); }
    };

    if (loading) return <div className="p-12 text-center text-ink-400"><Loader2 className="animate-spin inline" /> Caricamento orari...</div>;

    // Group slots by day
    const slotsByDay = {};
    (data?.slots || []).forEach((s) => {
        const day = s.start_at.slice(0, 10);
        if (!slotsByDay[day]) slotsByDay[day] = [];
        slotsByDay[day].push(s);
    });
    const days = Object.keys(slotsByDay).slice(0, 14);
    const fmtDay = (d) => { const dt = new Date(d); return dt.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" }); };
    const fmtTime = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

    if (confirmation) {
        return (
            <div data-testid="visit-confirmed" className="p-10 text-center max-w-md mx-auto">
                <div className="w-16 h-16 bg-gold/20 border border-gold flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={32} className="text-gold" />
                </div>
                <h3 className="font-serif text-3xl text-ink-50">Richiesta inviata</h3>
                <p className="text-sm text-ink-400 mt-4">Riceverai una conferma via email entro <strong className="text-gold">4 ore</strong>. Stato: <em>in attesa di conferma agenzia</em>.</p>
                <p className="text-xs text-ink-400 mt-6">📅 {new Date(confirmation.start_at).toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</p>
                <button onClick={onClose} className="cta-gold px-6 py-2.5 text-sm mt-8">Chiudi</button>
            </div>
        );
    }

    return (
        <div data-testid="book-visit" className="p-6 sm:p-10 max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Calendar size={18} className="text-gold" />
                    <span className="label-eyebrow">Prenota una visita</span>
                </div>
                <button onClick={onClose} className="text-ink-400 hover:text-ink-100"><X size={20} /></button>
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-ink-50 leading-tight">{propertyTitle}</h2>
            <p className="text-xs text-ink-400 mt-2 flex items-center gap-2"><Clock size={11} /> Durata visita: {data?.settings?.slot_duration_min || 60} min</p>

            {selectedSlot ? (
                <div className="mt-8">
                    <button onClick={() => setSelectedSlot(null)} className="text-xs text-gold hover:underline flex items-center gap-1 mb-4"><ChevronLeft size={12} /> Cambia orario</button>
                    <div className="border border-gold/40 bg-gold/5 p-4 mb-6">
                        <div className="text-xs text-ink-400 uppercase tracking-wider">Slot selezionato</div>
                        <div className="font-serif text-xl text-ink-50 mt-1">{new Date(selectedSlot.start_at).toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Telefono (consigliato)</label>
                            <input data-testid="visit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 ..." className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs text-ink-400 mb-2 uppercase tracking-wider">Messaggio per l'agenzia</label>
                            <textarea data-testid="visit-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Sono interessato a una visita..." className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm" />
                        </div>
                        {error && <div className="p-3 border border-red-700/40 bg-red-900/20 text-red-200 text-xs">{error}</div>}
                        <button data-testid="visit-confirm-btn" onClick={book} disabled={submitting} className="w-full cta-gold py-3.5 text-sm">{submitting ? "Invio richiesta..." : "Conferma richiesta di visita →"}</button>
                        <p className="text-[11px] text-ink-400 text-center">L'agenzia conferma entro 4 ore. Riceverai un'email automatica.</p>
                    </div>
                </div>
            ) : (
                <div className="mt-8 space-y-5 max-h-[480px] overflow-y-auto pr-2">
                    {error && <div className="p-3 border border-red-700/40 bg-red-900/20 text-red-200 text-xs">{error}</div>}
                    {days.length === 0 ? (
                        <p className="text-sm text-ink-400 text-center py-8">Nessuno slot disponibile nei prossimi 14 giorni. Contatta direttamente l'agenzia.</p>
                    ) : days.map((d) => (
                        <div key={d}>
                            <div className="text-xs text-ink-400 uppercase tracking-wider mb-2 capitalize">{fmtDay(d)}</div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                {slotsByDay[d].map((s) => (
                                    <button
                                        key={s.start_at}
                                        data-testid={`slot-${s.start_at}`}
                                        onClick={() => setSelectedSlot(s)}
                                        className="py-2.5 text-xs border border-white/15 hover:border-gold hover:bg-gold/10 hover:text-gold transition-colors text-ink-100 font-mono"
                                    >
                                        {fmtTime(s.start_at)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
